// Parameter field names, shared by Layout.js (the schema) and the page (formRefs lookups). Declared
// once so a rename cannot silently break the page's imperative field access, which is keyed by these
// exact strings.
export const FIELD = Object.freeze({
    Comment: "Comment",
});

/**
 * WMTRANSACTIONHEADER.STATUS. Mirrors Argos.Core/Constants -> TransactionStatusConstants.
 *
 * Only these three are reachable in this screen's result set: the procedure excludes 'Z' (Committed)
 * with `WMTRANSACTIONHEADER.Status <> 'Z'`, and O/H/L/Z is the whole set the WM transaction data-entry
 * screen offers.
 *
 * Note uWMM0077.dfm declares the third code as 'R', not 'L'. That is stale — every WM screen in this
 * app codes "Ready To Commit Later" as 'L' — and the server-side catalogue
 * (WMTransferShipmentsToDispatch.cs) corrects it the same way.
 */
export const Status = Object.freeze({
    Open: "O",
    Hold: "H",
    ReadyToCommitLater: "L",
});

/**
 * WMTRANSACTIONHEADER.STAGE. Mirrors Argos.Core/Constants -> TransactionStageConstants.
 *
 * The full stage set is P/F/N/R/C, but WMD0077 only ever offered Ready and Completed: a receipt or
 * shipment below Ready has nothing to hand to Freight Management. Captions are the DFM's verbatim.
 */
export const Stage = Object.freeze({
    Ready: "R",
    Completed: "C",
});

// Grid <Lookup> sources. DevExtreme's default lookup keys, hence id/text.
export const STATUS_OPTIONS = [
    { id: Status.Open, text: "Open" },
    { id: Status.Hold, text: "Hold" },
    { id: Status.ReadyToCommitLater, text: "Ready To Commit Later" },
];

export const STAGE_OPTIONS = [
    { id: Stage.Ready, text: "Ready To Receive/Pick/Staged" },
    { id: Stage.Completed, text: "Completed/Loaded/Putaway" },
];

// Saved-layout identity for the Preview grid, and GridLayoutManager's built-in layout name used as the
// initial currentLayout state.
export const PREVIEW_GRID_KEY = "7C4E1A93-2D68-4F05-B1E7-3A96D0C5E284";
export const DEFAULT_LAYOUT_NAME = "Default";

// Dynamic-form layout identity for the parameters panel.
export const LAYOUT_ID = "9B2F5D71-6C34-4A18-8E0D-52C7B4A9F613";
export const LAYOUT_NAME = "WMTransferShipmentsToDispatchForm";

// Filter-field definitions come from Argos.Core/Report/WM/WMTransferShipmentsToDispatch.cs via
// ReportFactory's "WMTRANSFERSHIPMENTSTODISPATCH" case.
export const REPORT_NAME = "WMTransferShipmentsToDispatch";
export const REPORT_FULL_NAME = "Argos.Report.WM.WMTransferShipmentsToDispatch, Argos.Report.WM";

export const PAGE_TITLE = "Transfer Shipments To Dispatch";

// The read-only memo beside the parameters (uWMD0077.dfm edtMemo), verbatim. It is the only place the
// screen explains which records qualify, so it is worth reproducing exactly rather than paraphrasing.
// No title: the Delphi memo sits at the top of the form with no caption of its own, so the web band
// carries none either. (The "Additional Information" caption belonged to the bottom-right group box
// this screen does not have.)
export const HELP_TEXT =
    "This program allows you to create FM Freight Bills, Dispatch Objects, OR Both based on FM Transfer "
    + "Flag in the transaction record. The transaction records must have \"Shipper, Consignee (shipment), "
    + "Transport Carrier and FM Profile\" in order to transfer.\n"
    + "The receipt/shipment transactions will be filtered based on the \"From Stage\" in the Transaction "
    + "Data Profile.";

// The legacy result dialog is TfrmABD0013, and uWMD0077.pas:145 passes this caption to it.
export const RESULT_CAPTION = "Transfer Receipts/Shipments To Freight Management";

export const RESULT_LABELS = Object.freeze({
    BatchNumber: "Batch Number",
    RecordsProcessed: "Records Processed",
});

export const RESULT_OK = "OK";

export const PREVIEW_TITLE = "Preview";

// Toolbar button captions, verbatim from the Delphi form (uWMD0077.dfm grpBottomButtons). Help and Exit
// are not migrated: Help opened a Delphi context topic and Exit closed the MDI child.
export const BUTTON = Object.freeze({
    Start: "Start",
    Preview: "Preview",
    Reset: "Reset",
});

// The procedure declares @InComment as Text, so this cap is ours, not the schema's. Kept in step with
// TransferToDispatchConstants.CommentMaxLength, which the service validates and rejects beyond.
export const COMMENT_MAX_LENGTH = 2000;

// The procedure alone is allowed 900s (uWMD0077.pas:172 sets CommandTimeOut := 900), and it loops a
// cursor over every matching header.
export const REQUEST_TIMEOUT_MS = 20 * 60 * 1000;
